'use strict'
import { Style, Text, Icon, Circle, Fill, Stroke } from 'ol/style'
import { METERS_PER_UNIT } from 'ol/proj/Units'
import { get } from 'ol/proj'
import { toRadians } from 'ol/math'

export function createStyleFunction (esriLayerInfoJson, layerConfig) {
    return new Promise((resolve, reject) => {
        const { featureStyles, labelStyles } = readEsriStyleDefinitions(esriLayerInfoJson.drawingInfo, layerConfig)
        for (let i = 0; i < featureStyles.length; i++) {
            featureStyles[i].style = createFeatureStyle(featureStyles[i])
        }
        for (let i = 0; i < labelStyles.length; i++) {
            labelStyles[i].maxResolution = getMapResolutionFromScale(labelStyles[i].maxScale || 1000)
            labelStyles[i].minResolution = getMapResolutionFromScale(labelStyles[i].minScale || 1)
            labelStyles[i].label = labelStyles[i].text
            labelStyles[i].style = new Style({
                text: createLabelStyle(labelStyles[i], layerConfig)
            })
        }

        const styleFunction = (feature, resolution) => {
            const styles = []
            const featureStyle = featureStyles.find(({ filters }) => {
                if (filters) {
                    return filters.every(({ field, value }) => {
                        const currentValue = feature.get(field)
                        if (!currentValue) return true // TODO: why?
                        const valuesIn = value.split(',').map(value => value.toString())
                        return valuesIn.indexOf(currentValue.toString()) > -1 // eslint-disable-line
                    })
                } else {
                    // will return the first style (default one)
                    return true
                }
            })

            if (featureStyle) {
                if (layerConfig?.features?.rotation) {
                    const imageStyle = featureStyle.style.getImage()
                    imageStyle.setRotation(+feature.getProperties().layerConfig.features.rotation * (Math.PI / 180))
                }
                styles.push(featureStyle.style)
            }

            // console.log(labelStyles)
            // debugger

            const labelStyle = labelStyles.find((label) => {
                return resolution <= (layerConfig?.labels?.resolution || 200)
            })

            if (labelStyle && labelStyle.style) {
                const text = getFormattedLabel(feature, labelStyle.label, layerConfig)
                labelStyle.style.getText().setText(text)
                styles.push(labelStyle.style)
            }

            // push labels!

            return styles.length > 0 ? styles : null

        }
        resolve(styleFunction)
    })
}

function readEsriStyleDefinitions ({ renderer, labelingInfo }, layerConfig) {
    if (!renderer) throw new Error('renderer is not defined')

    /**
     * @type {Array<import("./types").StyleType>}
     */
    const featureStyles = []
    /**
     * @type {Array<import("./types").LabelType>}
     */
    const labelStyles = labelingInfo ? readLabels(labelingInfo) : []
    let uniqueFieldValues
    switch (renderer.type) {
        case 'simple':
            featureStyles.push(readSymbol(renderer.symbol))
            break
        case 'uniqueValue':
            uniqueFieldValues = filterUniqueValues(renderer.uniqueValueInfos, renderer.fieldDelimiter)

            for (let i = 0; i < uniqueFieldValues.length; i++) {
                const uniqueField = uniqueFieldValues[i]

                /**
                 * @type {Array<import("./types").FilterType>}
                 */
                const filters = []

                if (renderer.field1) {
                    filters.push({
                        field: renderer.field1,
                        operator: 'in',
                        value: uniqueField.field1Values
                    })
                }
                if (renderer.field2) {
                    filters.push({
                        field: renderer.field2,
                        operator: 'in',
                        value: uniqueField.field2Values
                    })
                }
                if (renderer.field3) {
                    filters.push({
                        field: renderer.field3,
                        operator: 'in',
                        value: uniqueField.field3Values
                    })
                }
                const style = readSymbol(uniqueField.symbol, layerConfig, uniqueField.title)
                featureStyles.push({
                    filters,
                    title: uniqueField.title,
                    ...style
                })
            }

            if (renderer.defaultSymbol) {
                featureStyles.push(readSymbol(renderer.defaultSymbol))
            }

            break
        default:
            throw new Error(`"Renderer type "${renderer.type}" is not implemented yet`)
    }

    return { featureStyles, labelStyles }
}

function readLabels (labelingInfo) {
    return labelingInfo.map((labelDefinition) => {
        const labelStyle = readSymbol(labelDefinition.symbol)
        const expression = labelDefinition.labelExpression || labelDefinition.labelExpressionInfo.expression
        labelStyle.maxScale = labelDefinition.minScale || 1000
        labelStyle.minScale = labelDefinition.maxScale || 0
        labelStyle.text = expression.replace(/\[/g, '{')
            .replace(/\]/g, '}')
            .replace(/ CONCAT  NEWLINE  CONCAT /g, '\n') // eslint-disable-line
            .replace(/ CONCAT /g, ' ')
        return labelStyle
    })
}

function readSymbol (symbol, layerConfig, title) {
    let scale
    switch (symbol.type) {
        case 'esriSMS':
            return {
                circle: {
                    radius: symbol.size / 2,
                    fill: symbol.color
                        ? {
                            color: `rgba(${symbol.color.join(',')})`
                        }
                        : null,
                    stroke: symbol.outline
                        ? {
                            color: `rgba(${symbol.outline.color.join(',')})`,
                            width: symbol.outline.width
                        }
                        : null
                }
            }
        case 'esriSLS':
            return {
                stroke: {
                    color: `rgba(${symbol.color.join(',')})`,
                    width: symbol.width
                }
            }
        case 'esriSFS':
            return {
                stroke: symbol.outline
                    ? {
                        color: `rgba(${symbol.outline.color.join(',')})`,
                        width: symbol.outline.width
                    }
                    : null,
                fill: { color: `rgba(${symbol.color.join(',')})` }
            }
        case 'esriPMS':
            if (layerConfig && layerConfig.customScales && layerConfig.customScales[title]) {
                scale = layerConfig.customScales[title]
            } else if (layerConfig && layerConfig.customScale) {
                if (layerConfig.filterScale) {
                    if (symbol.url === layerConfig.filterScale) {
                        scale = layerConfig.customScale
                    }
                } else {
                    scale = layerConfig.customScale
                }
            } else if (symbol.width && symbol.height && layerConfig && layerConfig.iconScaleFactor) {
                scale = (layerConfig.iconScaleFactor * Math.max(symbol.width, symbol.height)) + 1
            } else {
                scale = 1
            }
            return {
                icon: {
                    src: `data:image/png;base64,${symbol.imageData}`,
                    rotation: symbol.angle,
                    scale
                }
            }
        case 'esriTS':
            return {
                text: symbol.text,
                font: symbol.font ? `${symbol.font.style} ${symbol.font.weight} ${symbol.font.size}pt ${symbol.font.family}` : '20px Calibri,sans-serif',
                offsetX: symbol.xoffset + 20,
                offsetY: symbol.yoffset - 10,
                textAlign: symbol.horizontalAlignment,
                textBaseline: symbol.verticalAlignment,
                padding: [5, 5, 5, 5],
                angle: symbol.angle,
                fill: symbol.color ? { color: `rgba(${symbol.color.join(',')})` } : null,
                stroke: symbol.haloColor
                    ? {
                        color: `rgba(${symbol.haloColor.join(',')}`,
                        width: symbol.haloSize ? symbol.haloSize : null
                    }
                    : null,
                backgroundFill: symbol.backgroundColor
                    ? {
                        fill: { color: `rgba(${symbol.backgroundColor.join(',')})` }
                    }
                    : null,
                backgroundStroke: symbol.borderLineColor
                    ? {
                        stroke: {
                            color: `rgba(${symbol.borderLineColor.join(',')})`,
                            width: symbol.borderLineSize || null
                        }
                    }
                    : null
            }
        default:
            throw new Error(`Symbol type "${symbol.type}" is not implemented yet`)
    }
}

function filterUniqueValues (styles, delimiter) {
    const uniqueSymbols = new Map()
    styles.forEach((s) => {
        if (!uniqueSymbols.has(s.label)) {
            uniqueSymbols.set(s.label, s.symbol)
        }
    })

    const result = []

    uniqueSymbols.forEach((symbol, label) => {
        const uniqueStyles = styles.filter((s) => {
            return s.label === label
        })
        const field1Values = new Set()
        const field2Values = new Set()
        const field3Values = new Set()
        uniqueStyles.forEach((s) => {
            field1Values.add(s.value.split(delimiter)[0])
            field2Values.add(s.value.split(delimiter)[1])
            field3Values.add(s.value.split(delimiter)[2])
        })

        result.push({
            title: label,
            symbol,
            field1Values: [...field1Values].join(),
            field2Values: [...field2Values].join(),
            field3Values: [...field3Values].join()
        })
    })

    return result
}

function getMapResolutionFromScale (scale) {
    const mpu = METERS_PER_UNIT[get('EPSG:3857').getUnits()]
    return scale / (mpu * 39.37 * (25.4 / 0.28))
}

function getFormattedLabel (feature, mask, layerConfig) {
    const props = feature.getProperties()
    return (layerConfig?.labels?.field) ? props[layerConfig.labels.field] : (props.Label || props.label)
    // if (mask.includes('$id')) mask = mask.replace('$id', feature.getId().toString())

    // if (mask.includes('{')) {
    //   return formatObject(mask, feature.getProperties())
    // } else {
    //   return mask
    // }
}

// function formatObject (mask, object, removeLeftovers = true) {
//   // return object.Label || ''
//   let result = mask
//   for (const name in object) {
//     const regEx = new RegExp('\\{' + name + '\\}', 'gm')
//     result = result.replace(regEx, object[name])
//   }

//   if (removeLeftovers) {
//     const regex = new RegExp('{([a-zA-Z]*?)}')
//     while (regex.exec(result) !== null) {
//       result = result.replace(regex, '')
//     }
//   }
//   return result
// }

function createFeatureStyle (styleData) {
    if (!styleData.icon) {
        styleData.icon = null
    }
    if (!styleData.font) {
        styleData.font = null
    }
    if (!styleData.fill) {
        styleData.fill = null
    }
    if (!styleData.stroke) {
        styleData.stroke = null
    }
    if (!styleData.circle) {
        styleData.circle = null
    }

    const fill = styleData.fill ? new Fill(styleData.fill) : null
    const stroke = styleData.stroke ? new Stroke(styleData.stroke) : null
    const fontSymbol = styleData.font ? createLabelStyle(styleData.font) : null
    let image = null
    let geometry = null

    if (styleData.icon) {
        geometry = function (feature) {
            const geom = feature.getGeometry()
            const geomType = geom.getType()
            return (
                geomType === 'Polygon' ? geom.getInteriorPoint()
                    : geomType === 'MultiPolygon' ? geom.getInteriorPoints()
                        : geom
            )
        }
        image = new Icon(Object.assign({}, styleData.icon, { rotation: toRadians(styleData.icon.rotation || 0) }))
    }
    if (styleData.circle) {
        const circleFill = styleData.circle.fill ? new Fill(styleData.circle.fill) : null
        const circleStroke = styleData.circle.stroke ? new Stroke(styleData.circle.stroke) : null
        image = new Circle({ radius: styleData.circle.radius, fill: circleFill, stroke: circleStroke })
    }

    return new Style({ stroke, fill, image, geometry, text: fontSymbol })
}

function createLabelStyle (labelData, layerConfig) {
    const rotation = (layerConfig?.labels?.rotation) || 0
    const backgroundFill = (layerConfig?.labels?.backgroundFill) ? new Fill(layerConfig.labels.backgroundFill) : null
    const fill = new Fill({ color: (layerConfig?.labels?.fill) || '#ffffff' })
    const backgroundStroke = (layerConfig?.labels?.backgroundStroke) ? new Stroke(layerConfig.labels.backgroundStroke) : null
    const stroke = new Stroke({ color: (layerConfig?.labels?.stroke) || '#000000', width: 2 })
    const font = (layerConfig?.labels?.font) || '14px Arial, Verdana, Courier New'
    const textAlign = (layerConfig?.labels?.textAlign) || 'center'
    const textBaseline = (layerConfig?.labels?.textBaseline) || 'middle'
    const offsetX = (layerConfig?.labels?.offsetX) || 0
    const offsetY = (layerConfig?.labels?.offsetY) || 0

    // text will be a template - can include: $id, {ATTRIBUTE_NAME}...
    // before the feature is drawn on the map it will be formatted based of feature attribute values
    // return new Text(Object.assign({}, labelData, { rotation, fill, stroke, backgroundFill, backgroundStroke, font }))
    return new Text(Object.assign({}, labelData, {
        rotation,
        fill,
        backgroundFill,
        stroke,
        backgroundStroke,
        font,
        textAlign,
        textBaseline,
        offsetX,
        offsetY
    }))
}
